/**
 * ============================================================================
 * HomeConnectModalFormNew — Model (form values + RHF resolver)
 * ============================================================================
 * Explicit resolver replaces register()-in-useEffect so controlled
 * setValue/watch inputs populate formState.errors on empty submit.
 * NO Yup — plain FieldErrors resolver matching Home's pre-MVC validators.
 * ============================================================================
 */

import type { FieldErrors, Resolver } from "react-hook-form";
import { SELECT_DEGREE, SELECT_PROGRAM } from "../utils";

export interface HomeConnectModalFormNewFormValues {
  fullName: string;
  countryCode: string;
  phoneNumber: string;
  emailId: string;
  degree: string;
  program: string;
  isTermsAccepted: boolean;
}

export const CODE = "Code";
export const NATIONAL_CODE = "+91";

export const degreeOptions = ["UG", "PG"];

export const errorAccordingToFieldName: Record<string, string> = {
  fullName: "Please enter valid full name",
  countryCode: "Please enter valid country code",
  phoneNumber: "Please enter valid phone number",
  emailId: "Please enter valid email address",
  degree: "Please select a degree",
  program: "Please select a program",
  isTermsAccepted: "Please accept terms & conditions",
};

/** Home-specific regexes (match pre-MVC utils.ts — do not swap for V2 variants). */
export const regexForInputs: Record<string, RegExp> = {
  fullName: /^[a-zA-Z][a-zA-Z ]{2,49}$/,
  countryCode: /^\+(?:[0-9]?){6,14}[0-9]$/,
  phoneNumberIndia: /^[1-9][0-9]{9}$/,
  phoneNumberIntl: /^[1-9][0-9]{6,14}$/,
  emailId:
    /^(?!.*\.\.)[a-zA-Z0-9](\.?[a-zA-Z0-9_-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
};

export interface CreateHomeConnectModalFormNewResolverOptions {
  /**
   * When true, degree/program are required.
   * False on new-lead first screen before programs are revealed.
   */
  requireProgramFields: boolean;
}

/**
 * Explicit RHF resolver — controlled inputs via setValue/watch.
 * Degree/program only required when requireProgramFields is true.
 */
export function createHomeConnectModalFormNewResolver(
  options: CreateHomeConnectModalFormNewResolverOptions,
): Resolver<HomeConnectModalFormNewFormValues> {
  const { requireProgramFields } = options;

  return (values) => {
    const errors: FieldErrors<HomeConnectModalFormNewFormValues> = {};

    if (!values.fullName || !regexForInputs.fullName.test(values.fullName)) {
      errors.fullName = {
        type: "validate",
        message: errorAccordingToFieldName.fullName,
      };
    }

    // Match pre-refactor: !!value && value !== CODE && regex.test(value)
    if (
      !values.countryCode ||
      values.countryCode === CODE ||
      !regexForInputs.countryCode.test(values.countryCode)
    ) {
      errors.countryCode = {
        type: "validate",
        message: errorAccordingToFieldName.countryCode,
      };
    }

    const phoneRegex =
      values.countryCode !== NATIONAL_CODE
        ? regexForInputs.phoneNumberIntl
        : regexForInputs.phoneNumberIndia;
    if (!values.phoneNumber || !phoneRegex.test(String(values.phoneNumber))) {
      errors.phoneNumber = {
        type: "validate",
        message: errorAccordingToFieldName.phoneNumber,
      };
    }

    if (!values.emailId || !regexForInputs.emailId.test(values.emailId)) {
      errors.emailId = {
        type: "validate",
        message: errorAccordingToFieldName.emailId,
      };
    }

    if (values.isTermsAccepted !== true) {
      errors.isTermsAccepted = {
        type: "validate",
        message: errorAccordingToFieldName.isTermsAccepted,
      };
    }

    if (requireProgramFields) {
      // Match pre-refactor: value !== SELECT_DEGREE && degreeOptions.includes(value)
      if (
        values.degree === SELECT_DEGREE ||
        !degreeOptions.includes(values.degree)
      ) {
        errors.degree = {
          type: "validate",
          message: errorAccordingToFieldName.degree,
        };
      }
      // Match pre-refactor: value !== SELECT_PROGRAM
      if (!values.program || values.program === SELECT_PROGRAM) {
        errors.program = {
          type: "validate",
          message: errorAccordingToFieldName.program,
        };
      }
    }

    const hasErrors = Object.keys(errors).length > 0;
    return hasErrors
      ? { values: {} as Record<string, never>, errors }
      : { values, errors: {} };
  };
}

/**
 * Optional GA overrides for FAQ / classic Home clones that share this controller.
 * Unset fields keep HomeNew defaults (parentSection / "Home Page" /
 * "Get Free Counselling").
 */
export interface HomeConnectModalFormNewAnalyticsOverrides {
  pageType?: string;
  sectionName?: string;
  ctaText?: string;
}

/**
 * OTP context template flag:
 * - newTemplate: HomeNew (setIsNewTemplete) — default
 * - newSignInFlow: classic HomeConnectModalForm (setIsNewSignInFlow)
 * - none: FAQ hub (neither)
 */
export type HomeConnectModalFormNewSignInFlowMode =
  | "newTemplate"
  | "newSignInFlow"
  | "none";

export interface HomeConnectModalFormNewProps {
  setMessage?: any;
  isOtpValidated?: (x: any, y?: any) => void;
  setFormResponse?: (x: any) => void;
  gaOtherPayloadInfo?: any;
  setFormValuesData?: (x: any) => void;
  isLandingPage?: boolean;
  associatedPrograms?: Array<ProgramDetails>;
  isNewLeadForm?: boolean;
  checkLeadFail?: (x: boolean) => void;
  isLambdaAPI?: boolean;
  isSpecificPage?: boolean;
  isFaq?: boolean;
  isProgram?: boolean;
  setApplicationRedirectLoaderDisabled?: (x: boolean) => void;
  setAPIResposeURL?: (x: string) => void;
  seatsFilled?: number;
  isSeatsFilled?: boolean;
  isProgramListPage?: boolean;
  isMobile?: boolean;
  landingPageValue?: string;
  /** Job-role (and similar) pages: show stats cards even when isProgram is true. */
  showStatsCards?: boolean;
  /** FAQ / classic clones — optional analytics hardcodes. */
  analyticsOverrides?: HomeConnectModalFormNewAnalyticsOverrides;
  /** OTP UI template flag (default: "newTemplate"). */
  signInFlowMode?: HomeConnectModalFormNewSignInFlowMode;
  /**
   * Classic HomeConnectModalForm quirk: set leadResponse early on success,
   * and force ID to "1" on the final success path.
   */
  forceLeadResponseIdToOne?: boolean;
  /**
   * FAQ hub: build program dropdown from global PROGRAM_DROPDOWN (+ India
   * exclusions) instead of associatedPrograms.
   */
  useGlobalProgramDropdown?: boolean;
  /**
   * When defined, includes `Portal` on the lead form payload (molecules Home
   * always sends Portal — "" or "Degree" for program pages).
   */
  portal?: string;
  /**
   * Lead Custom5 value (default "False"). Mba uses "Not Verified".
   */
  custom5Value?: string;
}
